import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { listCorpora, type CorpusSummary } from "../../../api/content/corporaApi";
import { ALL_CORPORA, FUZZY_FEATURE, readSearchEngine, searchCorpora, type CorpusSearchHit, type CorpusSearchResult, type CorpusSearchScope } from "../../../api/content/searchApi";
import { formatCollectionVersionRef, parseCollectionVersionRef, SEGMENT_SEPARATOR, type CollectionVersionRef, type ContentAddress } from "../../../shared/references/contentReferences";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useQueryAfterTypingPause } from "../../../shared/loading/afterTypingPause";
import { useLoad } from "../../../shared/loading/useLoad";
import { TabOpenActions, useWorkspace, WorkspaceLink, type AppPath, type Placement } from "../../../shell/workspace";
import AppButton from "../../../ui/buttons/AppButton";
import EmptyState from "../../../ui/feedback/EmptyState";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import AttachedStatus from "../attached/AttachedStatus";
import SearchFilterControls from "../filters/SearchFilterControls";
import { NO_SEARCH_FILTERS, segmentTypesOf, type SearchFilters } from "../filters/searchFilters";
import { shownKey, useKeyForm } from "../keys/keyForm";
import HighlightedSnippet from "../query/HighlightedSnippet";
import SearchQueryField from "../query/SearchQueryField";
import type { FuzzySupport } from "../query/SearchSyntaxHelp";
import CorpusChoice from "./CorpusChoice";
import { findNamedAddress, type NamedAddress } from "./documentAddress";
import { isFuzzyRefusal } from "./fuzzyRefusal";

/** Hits per "Show more". */
const HITS_PER_STEP = 20;
const NO_CORPORA: readonly CorpusSummary[] = [];

/** The corpora still ticked; `null` when the user unticked them all. */
function scopeOf(corpora: readonly CorpusSummary[], excluded: ReadonlySet<string>): CorpusSearchScope | null {
  if (excluded.size === 0) return ALL_CORPORA;
  const corpusIds = corpora.map((corpus) => corpus.corpusId).filter((corpusId) => !excluded.has(corpusId));
  return corpusIds.length === 0 ? null : { corpusIds, versions: [] };
}

/**
 * What a result does, as its host decides: open a page, as a link, so Ctrl+click, a middle
 * click and Alt+click open it in a new tab or beside, like every link (F90 Q2, TR-B4-02); or
 * be picked, as a Thread's input list picks documents. A host that opens results opens what the
 * field names too: a document's version or segment typed after its key or as a reference (F135).
 */
export type CorpusHitAction = OpenHitAction | { readonly kind: "pick"; readonly onPick: (hit: CorpusSearchHit) => void };

/** Results open their page at `routeOf`; a plain click and Enter open it at `placement` (F140: the Documents sidebar's beside). */
interface OpenHitAction {
  readonly kind: "open";
  readonly routeOf: (address: ContentAddress) => AppPath;
  readonly placement: Placement;
}

export interface CorpusSearchProps {
  projectId: string;
  hitAction: CorpusHitAction;
  /**
   * The corpora as a host that already holds them gives them: the Documents sidebar's list and
   * the ones it leaves out (F20). The search then neither loads them nor offers a choice of its own.
   */
  corpora?: { readonly list: readonly CorpusSummary[]; readonly excluded: ReadonlySet<string> };
  /** One exact corpus version to search, such as the one a Thread binds: no choice of corpora then. */
  version?: CollectionVersionRef;
  /**
   * The filters as a host that shares them gives them: the Documents sidebar's lists follow the
   * same data attached and kind of segment (F88, F141). Without them, the search keeps its own.
   */
  filters?: { readonly value: SearchFilters; readonly onChange: (filters: SearchFilters) => void };
  /**
   * The field's text as a host that follows it gives it: the Documents sidebar sets its lists aside
   * while there is a query. Without it, the search keeps its own.
   */
  query?: { readonly value: string; readonly onChange: (query: string) => void };
}

/** What the search found: the documents its text matches, or the one document, version or segment its text names (F135). */
type Found =
  | { readonly kind: "hits"; readonly result: CorpusSearchResult }
  | { readonly kind: "named"; readonly named: NamedAddress };

/** One result: what it opens, and the document it shows. */
interface ResultRow {
  readonly id: string;
  readonly address: ContentAddress;
  /** The document's key, in full and as its corpus writes it short (F136). */
  readonly key: string;
  readonly shortKey: string | null;
  /** The document as the search found it; `null` for one named by its reference. */
  readonly hit: CorpusSearchHit | null;
}

/** A hit opens its whole document, at the version found (F140), never only its best segment. */
function rowsOf(found: Found): readonly ResultRow[] {
  if (found.kind === "named") {
    const { address, documentKey, hit } = found.named;
    return [{ id: "named", address, key: documentKey, shortKey: hit?.shortKey ?? null, hit }];
  }
  return found.result.hits.map((hit) => ({
    id: `${hit.document.documentId}@${hit.document.version}`,
    address: { document: hit.document, segmentPath: null },
    key: hit.documentKey,
    shortKey: hit.shortKey,
    hit,
  }));
}

/**
 * DMW's one search over the project's corpora (F20, F31, F91; content.md D-16), in one field
 * for keys, references and text (F135): a query in the search syntax, the corpora to cover,
 * the data the documents must have attached, and the documents found with their most developed
 * form. A document's key finds it first; where results open, a key or reference with a version
 * or segment names that part, as the field's one result. Enter takes the first result, as an
 * address bar does. It neither routes nor starts work; the host does both through `hitAction`.
 */
export default function CorpusSearch(props: CorpusSearchProps) {
  const { hitAction } = props;
  if (hitAction.kind === "open") return <OpeningSearch {...props} action={hitAction} />;
  return <SearchBody {...props} activate={(row) => { if (row.hit !== null) hitAction.onPick(row.hit); }} />;
}

/** A search whose results open their page, through the workspace's one `open`, where the host says. */
function OpeningSearch(props: CorpusSearchProps & { action: OpenHitAction }) {
  const workspace = useWorkspace();
  const { action } = props;
  return <SearchBody {...props} activate={(row) => { workspace.open(action.routeOf(row.address), { placement: action.placement }); }} />;
}

/** The search itself; `activate` does what a result does, for a click and for Enter alike. */
function SearchBody({ projectId, hitAction, corpora: hosted, version, filters: hostedFilters, query: hostedQuery, activate }: CorpusSearchProps & { activate: (row: ResultRow) => void }) {
  const { t } = useTranslation();
  const keyForm = useKeyForm();
  const [ownQuery, setOwnQuery] = useState("");
  const query = hostedQuery?.value ?? ownQuery;
  const [ownExcluded, setExcluded] = useState<ReadonlySet<string>>(new Set());
  const excluded = hosted?.excluded ?? ownExcluded;
  const [ownFilters, setOwnFilters] = useState<SearchFilters>(NO_SEARCH_FILTERS);
  const filters = hostedFilters?.value ?? ownFilters;
  const [limit, setLimit] = useState(HITS_PER_STEP);
  const searched = useQueryAfterTypingPause(query);
  const engine = useLoad(readSearchEngine).state;
  const hostedCorpora = hosted !== undefined;
  const opensResults = hitAction.kind === "open";
  const corpora = useLoad(useCallback(
    (signal: AbortSignal) => (hostedCorpora ? Promise.resolve(NO_CORPORA) : listCorpora(projectId, signal)),
    [projectId, hostedCorpora],
  )).state;
  const corpusList = hosted?.list ?? (corpora.status === "loaded" ? corpora.value : NO_CORPORA);
  // By its text, so a host that builds the same version anew each render doesn't search again.
  const versionKey = version === undefined ? null : formatCollectionVersionRef(version);
  const scope = useMemo((): CorpusSearchScope | null => {
    if (versionKey === null) return scopeOf(corpusList, excluded);
    const exact = parseCollectionVersionRef(versionKey);
    return exact === null ? null : { corpusIds: [], versions: [exact] };
  }, [corpusList, excluded, versionKey]);
  const searchedCorpora = corpusList.filter((corpus) => versionKey === null ? !excluded.has(corpus.corpusId) : corpus.corpusId === version?.collectionId);
  const load = useCallback(
    async (signal: AbortSignal): Promise<Found | null> => {
      if (searched === "" || scope === null) return null;
      const named = opensResults ? await findNamedAddress(projectId, searched, { scope, attached: filters.attached }, signal) : null;
      if (named !== null) return { kind: "named", named };
      const request = { query: searched, scope, searchIn: filters.searchIn, attached: filters.attached, limit };
      return { kind: "hits", result: await searchCorpora(projectId, request, signal) };
    },
    [projectId, searched, scope, filters, limit, opensResults],
  );
  const { state: results, reload } = useLoad(load);
  const rows = results.status === "loaded" && results.value !== null ? rowsOf(results.value) : null;
  const hasMore = results.status === "loaded" && results.value?.kind === "hits" && results.value.result.hits.length >= limit;
  const activateFirst = () => {
    const first = rows?.[0];
    if (first !== undefined) activate(first);
  };
  // Enter before the search has answered takes the answer to what was typed, never an older one.
  const enterPressedFor = useRef<string | null>(null);
  const activateFirstOnceAnswered = useEffectEvent(activateFirst);
  useEffect(() => {
    if (enterPressedFor.current !== searched || results.status === "loading") return;
    enterPressedFor.current = null;
    activateFirstOnceAnswered();
  }, [results, searched]);
  const takeFirst = () => {
    const typed = query.trim();
    if (typed === "") return;
    if (typed === searched && results.status !== "loading") activateFirst();
    else enterPressedFor.current = typed;
  };
  const fuzzy: FuzzySupport = engine.status !== "loaded" ? "unknown" : engine.value.features.includes(FUZZY_FEATURE) ? "available" : "unavailable";
  // A hit names its corpus only while several are searched; over one the name would repeat (C2).
  const namesCorpora = searchedCorpora.length > 1;
  const corpusName = new Map(corpusList.map((corpus) => [corpus.corpusId, corpus.name]));
  const corpusOf = (row: ResultRow) => row.hit === null || !namesCorpora ? null : (corpusName.get(row.hit.corpusVersion.collectionId) ?? row.hit.corpusVersion.collectionId);
  const changeQuery = (next: string) => { (hostedQuery?.onChange ?? setOwnQuery)(next); setLimit(HITS_PER_STEP); enterPressedFor.current = null; };
  const changeExcluded = (next: ReadonlySet<string>) => { setExcluded(next); setLimit(HITS_PER_STEP); };
  const changeFilters = (next: SearchFilters) => { (hostedFilters?.onChange ?? setOwnFilters)(next); setLimit(HITS_PER_STEP); };

  return (
    <div className="space-y-2">
      <SearchQueryField query={query} onQueryChange={changeQuery} onEnter={takeFirst} fuzzy={fuzzy} label={t("search.corpora.field")} keyHelp={opensResults ? "opensAddress" : "findsDocument"} />
      <SearchFilterControls projectId={projectId} filters={filters} segmentTypes={segmentTypesOf(searchedCorpora)} onFiltersChange={changeFilters} />
      {!hostedCorpora && versionKey === null && corpusList.length > 1 ? <CorpusChoice corpora={corpusList} excluded={excluded} onExcludedChange={changeExcluded} /> : null}
      {corpora.status === "failed" ? <ErrorNotice error={corpora.error} compact /> : null}
      {scope === null ? <EmptyState size="small">{t("search.corpora.noneTicked")}</EmptyState> : null}
      {results.status === "failed" && isFuzzyRefusal(results.error) ? (
        <StatusMessage tone="warning">{t("search.fuzzyRefused")}</StatusMessage>
      ) : results.status !== "loaded" ? (
        <LoadStatus state={results} loadingLabel={t("search.searching")} onRetry={reload} compact />
      ) : rows === null ? null : rows.length === 0 ? (
        <EmptyState size="small">{t("search.noHits")}</EmptyState>
      ) : (
        <>
          <ul className="space-y-1">
            {rows.map((row) => (
              <li key={row.id}>
                <ResultRowControl row={row} action={hitAction} onActivate={() => { activate(row); }}>
                  <ResultRowContent row={row} shown={shownKey(row, keyForm)} corpusName={corpusOf(row)} />
                </ResultRowControl>
              </li>
            ))}
          </ul>
          {hasMore ? (
            <AppButton variant="secondary" size="xs" onClick={() => { setLimit(limit + HITS_PER_STEP); }}>{t("search.showMore")}</AppButton>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * A result: the document's whole key on a line of its own (F139), in the form chosen (F136),
 * with the segment it opens at, if named; then its most developed form, its corpus where
 * several are searched, and where it matches.
 */
function ResultRowContent({ row, shown, corpusName }: { row: ResultRow; shown: string; corpusName: string | null }) {
  return (
    <>
      <span className="app-title block text-sm font-semibold [overflow-wrap:anywhere]">
        {shown}
        {row.address.segmentPath === null ? null : <span className="app-text-muted font-normal"> {SEGMENT_SEPARATOR}{row.address.segmentPath}</span>}
      </span>
      {row.hit === null ? null : (
        <>
          {row.hit.attached.length === 0 && corpusName === null ? null : (
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <AttachedStatus forms={row.hit.attached} />
              {corpusName === null ? null : <span className="app-text-muted text-xs">{corpusName}</span>}
            </span>
          )}
          <HighlightedSnippet snippet={row.hit.snippet} />
        </>
      )}
    </>
  );
}

const ROW_CLASS = "app-card block w-full min-w-0 space-y-0.5 p-2 text-left hover:shadow";

/**
 * A result that opens its page is a link that a plain click opens where the host says, with
 * the other places beside it (F140); one that is picked is a button.
 */
function ResultRowControl({ row, action, onActivate, children }: { row: ResultRow; action: CorpusHitAction; onActivate: () => void; children: ReactNode }) {
  if (action.kind === "pick") {
    return <button type="button" className={ROW_CLASS} onClick={onActivate}>{children}</button>;
  }
  const route = action.routeOf(row.address);
  return (
    <div className="flex items-start gap-1">
      <WorkspaceLink route={route} placement={action.placement} className={ROW_CLASS}>{children}</WorkspaceLink>
      <TabOpenActions route={route} plainClick={action.placement} />
    </div>
  );
}
